/**
 * Email Service — Thin wrapper for calling the send-email Edge Function.
 *
 * Fire-and-forget semantics: never throws, always returns { success }.
 * Callers should not await this if email failure shouldn't block their flow.
 */

import { captureException, captureMessage } from "../lib/sentryLazy";
import { supabase } from "../lib/supabase";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EmailTemplate =
  | "welcome"
  | "payment_confirmation"
  | "packet_ready"
  | "wizard_abandonment"
  | "filing_reminder_30day"
  | "filing_reminder_14day"
  | "filing_reminder_7day"
  | "filing_reminder_3day"
  | "filing_confirmation"
  | "hearing_prep"
  | "outcome_request"
  | "savings_celebration"
  | "referral_request"
  | "shield_upsell"
  | "annual_reengagement"
  | "subscription_renewal"
  | "educational_drip"
  | "tax_season_summary"
  | "packet_generation_issue";

interface EmailData {
  fullName?: string;
  propertyAddress?: string;
  amount?: number;
  sessionId?: string;
  deadline?: string;
  hearingDate?: string;
  hearingTime?: string;
  hearingLocation?: string;
  // Payment confirmation
  last4?: string;
  // Packet ready
  downloadUrl?: string;
  // Outcome & celebration
  savings?: string;
  roiPercent?: string;
  // Referral
  referralUrl?: string;
  // Annual re-engagement
  lastYearValue?: string;
  thisYearValue?: string;
  change?: string;
  changePercent?: string;
  isShieldSubscriber?: boolean;
  // Subscription renewal
  renewalDate?: string;
  // Tax season summary
  totalSavings?: string;
  outcome?: string;
  assessed?: string;
  // Shield upsell
  shieldUrl?: string;
  // Educational drip
  daysUntilDeadline?: string;
  year?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Days remaining until the protest deadline. Caller passes the ISO date from CIE config. */
export function daysUntilDeadline(protestDeadline: string): number {
  const deadline = new Date(`${protestDeadline}T23:59:59-05:00`);
  return Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 86_400_000));
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Send a transactional email via the send-email Edge Function.
 *
 * Never throws. Returns { success: true } if the Edge Function accepted
 * the request, or { success: false } on any error.
 */
export async function sendEmail(
  template: EmailTemplate,
  to: string,
  data: EmailData = {}
): Promise<{ success: boolean }> {
  try {
    const { error } = await supabase.functions.invoke("send-email", {
      body: { to, template, data },
    });

    if (error) {
      captureMessage(`[email] ${template} to ${to} failed: ${error.message}`, "warning");
      return { success: false };
    }

    return { success: true };
  } catch (err) {
    captureException(err instanceof Error ? err : new Error(String(err)));
    return { success: false };
  }
}
